import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import ParticleField from '../components/ParticleField';
import FloatingLogo from '../components/FloatingLogo';
import { Reveal } from '../components/Footer';
import { CtaBand } from './About';
import { brand, services, engagement } from '../mock';

export default function Services() {
  return (
    <div className="App">
      <section className="hero-dark relative overflow-hidden">
        <ParticleField density={0.9} />
        <div className="absolute inset-0 hidden md:block pointer-events-none">
          <FloatingLogo src={brand.logoTransparent} size={190} speed={0.5} vary />
        </div>
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 pt-40 pb-20 md:pt-44 md:pb-24">
          <Reveal className="mx-auto max-w-4xl text-center">
            <p className="eyebrow text-[#c6a94b]">Services</p>
            <h1 className="mt-5 font-serif text-[36px] md:text-[56px] leading-[1.08] text-white">
              Governed capability, delivered with discipline
            </h1>
            <p className="mt-6 mx-auto max-w-2xl text-[17px] leading-relaxed text-[#c4d0cd]">
              Advisory and delivery services that move you from AI ambition to accountable enterprise capability - engaged flexibly around your priorities.
            </p>
          </Reveal>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-b from-transparent to-[#faf9f5]" />
      </section>

      {/* Services list */}
      <section className="bg-[#faf9f5] py-24">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 space-y-6">
          {services.map((s, i) => (
            <Reveal key={s.name} delay={i * 70}>
              <div className="lift grid gap-8 rounded-2xl border border-[#eceae2] bg-white p-8 md:p-10 lg:grid-cols-12 lg:items-center">
                <div className="lg:col-span-7">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-[26px] text-[#c6a94b]">0{i + 1}</span>
                    <span className="inline-block rounded-full bg-[#eef3f1] px-3 py-1 text-[11px] font-semibold tracking-wide text-[#1f5866]">{s.tag}</span>
                  </div>
                  <h2 className="mt-4 font-serif text-[30px] md:text-[36px] text-[#22312f]">{s.name}</h2>
                  <p className="mt-3 text-[16px] leading-relaxed text-[#5a6668] max-w-xl">{s.body}</p>
                </div>
                <div className="lg:col-span-5">
                  <ul className="grid gap-3">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-3 rounded-xl bg-[#faf9f5] border border-[#eceae2] px-4 py-3">
                        <Check className="h-4.5 w-4.5 text-[#c6a94b]" />
                        <span className="text-[14.5px] text-[#22312f]">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How engagements are delivered */}
      <section className="bg-white py-24 border-y border-[#eceae2]">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
          <Reveal className="mx-auto max-w-4xl text-center">
            <p className="eyebrow text-[#1f5866]">How engagements work</p>
            <h2 className="mt-4 font-serif text-[32px] md:text-[44px] leading-tight text-[#22312f]">A clear, collaborative path</h2>
            <p className="mt-5 mx-auto max-w-2xl text-[16px] leading-relaxed text-[#5a6668]">
              We work with you, we help, and together we design - founder-led, with specialist collaborators engaged where required.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {engagement.map((e, i) => (
              <Reveal key={e.title} delay={i * 90}>
                <div className="relative h-full rounded-2xl border border-[#eceae2] bg-[#faf9f5] p-8">
                  <div className="absolute -top-4 left-8 flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#2b6d7c] to-[#c6a94b] font-serif text-[18px] text-white">
                    {i + 1}
                  </div>
                  <h3 className="mt-4 font-serif text-[24px] text-[#22312f]">{e.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#5a6668]">{e.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry types */}
      <section className="bg-[#faf9f5] py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 text-center">
          <Reveal>
            <p className="eyebrow text-[#c6a94b]">Enquiries we accept</p>
            <h2 className="mt-4 font-serif text-[32px] md:text-[42px] leading-tight text-[#22312f]">
              We welcome focused discovery conversations
            </h2>
            <p className="mt-5 text-[16px] leading-relaxed text-[#5a6668] max-w-2xl mx-auto">
              Whether you are shaping an AI strategy, need governed capability designed, or want decision support delivered; tell us your challenge and we’ll suggest a practical next step.
            </p>
            <Link to="/contact" className="mt-8 inline-flex">
              <Button className="btn-gold rounded-full h-12 px-7 text-[14px] font-semibold">
                Discuss Your AI Requirements <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
